import React, {createContext, PropsWithChildren, useContext, useEffect} from "react"
import useAsyncResultIdle from "../hooks/useAsyncResultIdle"
import {getPermissions, Permissions} from "../resources/permissions"
import {useAccessToken, useBankUserBankId, useIsAuthenticated, useIsBankUser, useIsOrgUser, useIsUnitUser} from "./auth"
import {claimsContext} from "./claims"
import {isBankManageResubmitApplication, isOrgManageResubmitApplication} from "../resources/orgPolicy"
import {Assignee} from "../resources/application"

const authContext = createContext<Map<string, [string]>>(new Map<string, [string]>())

export function PermissionProvider({children}: PropsWithChildren<{}>) {
	let accessToken: string | undefined = undefined
	const [getPermissionsState, getPermissionsFunc] = useAsyncResultIdle(getPermissions)

	useEffect(() => {
		if (accessToken !== undefined) {
			getPermissionsFunc(accessToken)
		}
	}, [accessToken, useIsAuthenticated()])

	function generatePermissions(t: Permissions) {
		const res = new Map<string, [string]>()
		t.attributes.permissions.map((p) => res.set(p.resource, p.actions))
		return res
	}

	if (useIsAuthenticated()) {
		accessToken = useAccessToken()
		return (
			<authContext.Provider
				value={getPermissionsState.match(
					() => new Map<string, [string]>(),
					() => new Map<string, [string]>(),
					(t) => generatePermissions(t),
					() => new Map<string, [string]>()
				)}
			>
				{children}
			</authContext.Provider>
		)
	} else {
		accessToken = undefined
		return <authContext.Provider value={new Map<string, [string]>()}>{children}</authContext.Provider>
	}
}

export function hasPermission(resource: string, action: string) {
	const permission = useContext(authContext)
	const resourceActions = permission.get(resource)
	if (resourceActions) {
		return resourceActions.includes(action)
	}
	return false
}

export function useIsAllowedToShowAssigneeFilter() {
	if (useIsUnitUser() || useIsBankUser()) {
		return true
	} else if (useIsOrgUser()) {
		const orgId = useContext(claimsContext)?.orgId
		return (
			!!orgId &&
			hasPermission("application.status", "update") &&
			hasPermission("application.document.status", "update") &&
			isOrgManageResubmitApplication(orgId)
		)
	}
	return false
}

export function useIsAllowedToEvaluate(
	relatedOrgId: string,
	forceAssigneeCheck: boolean,
	assignee?: Assignee
): boolean {
	if (useIsUnitUser()) {
		return true
	} else if (useIsOrgUser()) {
		const orgId = useContext(claimsContext)?.orgId
		return (
			!!orgId &&
			hasPermission("application.status", "update") &&
			hasPermission("application.document.status", "update") &&
			isOrgManageResubmitApplication(orgId)
		)
	} else if (useIsBankUser()) {
		const bankId = useContext(claimsContext)?.bankId
		return (
			!!bankId &&
			!!relatedOrgId &&
			hasPermission("application.status", "update") &&
			hasPermission("application.document.status", "update") &&
			isBankManageResubmitApplication(bankId, relatedOrgId) &&
			(!forceAssigneeCheck || assignee === Assignee.Bank)
		)
	}
	return false
}

export function useIsAllowResubmit(
	enableReevaluateApplication: boolean,
	relatedOrgId: string,
	assignee: Assignee
): boolean {
	return (
		enableReevaluateApplication &&
		hasPermission("application.resubmit", "create") &&
		useIsManageResubmitApplication(relatedOrgId, assignee)
	)
}

export function useIsAllowUpdateDocument(relatedOrgId: string, assignee: Assignee): boolean {
	return hasPermission("application.document", "update") && useIsManageResubmitApplication(relatedOrgId, assignee)
}

function useIsManageResubmitApplication(relatedOrgId: string, assignee: Assignee): boolean {
	const bankId = useBankUserBankId()

	return (
		useIsUnitUser() ||
		useIsOrgUser() ||
		(useIsBankUser() && isBankManageResubmitApplication(bankId, relatedOrgId) && assignee === Assignee.Bank)
	)
}
